import { useEffect } from 'react'
import {
  AncillariesLocation,
  CarHireLocation,
  LocationSearchParams,
} from '@reward-platform/ancillaries-locations'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  getHotelLocations,
  getCarLocations,
  getExperienceLocations,
} from '~/services/locationService'
import { usePlatformConfig } from '~/context/platform'
import { useNotification } from '~/context/notification'
import { GenericClientError } from '~/utils/errors'

type LocationSearchType = 'HOTEL' | 'CAR_HIRE' | 'EXPERIENCE'

type QueryOptions = {
  isEnabled?: boolean
}

type SearchTypeLocation<T extends LocationSearchType> = {
  HOTEL: AncillariesLocation
  CAR_HIRE: CarHireLocation
  EXPERIENCE: AncillariesLocation
}[T]

type QueryFunction<T extends LocationSearchType> = (
  params: LocationSearchParams
) => Promise<SearchTypeLocation<T>[]>

type SearchParams = Omit<LocationSearchParams, 'locale'>

export const createAncillariesLocationsQueryKey = (
  searchType: LocationSearchType,
  params: SearchParams,
  opCo: string,
  locale: string
) => {
  const { latitude, longitude, vendorBrandId, multiBrand } = params
  const searchString = latitude && longitude ? undefined : params.searchString
  // vendorBrandId must be part of the key: with the app-wide staleTime of Infinity, a
  // brand-filtered and an unfiltered list for the same search string would otherwise share
  // one cache entry and whichever was fetched first would be served for both.
  return [
    'anx_locations_search',
    searchType,
    searchString,
    latitude,
    longitude,
    vendorBrandId,
    multiBrand,
    opCo,
    locale,
  ]
}

export const useAncillariesLocationsSearch = <T extends LocationSearchType>(
  searchType: T,
  params: SearchParams,
  options: QueryOptions = { isEnabled: true }
) => {
  const { opCo, locale } = usePlatformConfig()
  const { addErrorCodeNotification } = useNotification()

  const queryFn = {
    HOTEL: getHotelLocations,
    CAR_HIRE: getCarLocations,
    EXPERIENCE: getExperienceLocations,
  }[searchType] as QueryFunction<T> | undefined

  if (!queryFn) {
    throw new Error(`Unsupported search type: ${searchType}`)
  }

  const { latitude, longitude, vendorBrandId, multiBrand } = params

  const searchString = latitude && longitude ? undefined : params.searchString
  const queryKey = createAncillariesLocationsQueryKey(searchType, params, opCo, locale)

  const queryState = useQuery<Array<SearchTypeLocation<T>>, Error>({
    queryKey,
    queryFn: () =>
      queryFn({
        searchString,
        latitude,
        longitude,
        vendorBrandId,
        ...(multiBrand ? { multiBrand: true } : {}),
        locale,
      }),
    placeholderData: keepPreviousData,
    enabled: Boolean(searchString || (latitude && longitude)) && options.isEnabled,
  })

  useEffect(() => {
    if (queryState.isError && queryState.error instanceof GenericClientError) {
      addErrorCodeNotification(`${searchType}_LOCATIONS_ERROR`, {
        type: 'ERROR',
        errorCode: queryState.error.code,
        statusCode: queryState.error.httpStatus,
      })
    }
  }, [addErrorCodeNotification, queryState.isError, queryState.error, searchType])

  return queryState
}
